import { Badge, Box, Flex, Heading } from '@radix-ui/themes';
import { CommonBackArrow } from 'components/common/buttons/back-arrow';
import { ErrorBoundary } from 'components/common/error-boundary';
import { TableProvider } from 'components/common/table/context';
import { PitchPerfDisplayOptions } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/controls/display-options';
import { PitchPerfGroupTable } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/group-table';
import { PitchPerfPlots } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/plots';
import { PitchPerfShotTableHoC } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/shot-table';
import usePitchPerformanceStore from 'components/sections/admin-portal/machines/dialogs/pitch-performance/store/use-pitch-performance-store';
import {
  CheckedContext,
  CheckedProvider,
} from 'contexts/layout/checked.context';
import { parseISO } from 'date-fns';
import { format } from 'date-fns-tz';
import { t } from 'i18next';
import { LOCAL_PRETTY_DATETIME_FORMAT } from 'lib_ts/enums/datetime.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IDataCollection } from 'lib_ts/interfaces/i-data-collection';
import { IMachine } from 'lib_ts/interfaces/i-machine';
import { useContext, useEffect, useMemo, useRef } from 'react';

interface IProps {
  machine: IMachine;
  hwd: IDataCollection;
  onBack: () => void;
}

export const HwDiagnosticDetailsHoC = (props: IProps) => {
  const groups = usePitchPerformanceStore((s) => s.groups);

  // we need to wrap everything in a table and checked provider
  // so that we can use the table to control the charts + metrics

  return (
    <ErrorBoundary identifier="PitchPerformanceHoC">
      <TableProvider identifier="PitchPerformanceTableProvider">
        <CheckedProvider data={groups}>
          <HwDiagnosticDetails {...props} />
        </CheckedProvider>
      </TableProvider>
    </ErrorBoundary>
  );
};

const HwDiagnosticDetails = (props: IProps) => {
  const filters = usePitchPerformanceStore((s) => s.filters);
  const groups = usePitchPerformanceStore((s) => s.groups);
  const refGroups = usePitchPerformanceStore((s) => s.refGroups);
  const selectedPitchId = usePitchPerformanceStore((s) => s.selectedPitchId);

  useEffect(() => {
    usePitchPerformanceStore
      .getState()
      .initialize(props.hwd.machineID, props.hwd.ball_type);

    return () => usePitchPerformanceStore.getState().reset();
  }, [props.hwd]);

  const { onBack, hwd: collection } = props;

  const { tally, checkedIDs, setChecked, resetChecked, getChecked } =
    useContext(CheckedContext);

  // array of pitch IDs checked-off at time of drill down
  const checkedGroups = useRef<string[]>([]);

  const hwdGroups = useMemo(() => {
    if (tally.checked === 0) {
      // nothing checked => everything is visible
      return groups;
    }

    return groups.filter((g) => getChecked(g._id));
  }, [groups, tally]);

  const rtGroups = useMemo(() => {
    const relevantPitchIDs = hwdGroups.map((g) => g._id);

    // only show pitches from RT that were fired in HWD
    const relevantGroups = refGroups.filter((g) =>
      relevantPitchIDs.includes(g._id)
    );

    if (tally.checked === 0) {
      // nothing checked => everything is visible
      return relevantGroups;
    }

    return relevantGroups.filter((g) => getChecked(g._id));
  }, [hwdGroups, refGroups, tally]);

  useEffect(() => {
    usePitchPerformanceStore.getState().setFirstGroups(hwdGroups);
  }, [hwdGroups]);

  // re-generate report whenever filters change
  useEffect(() => {
    resetChecked();

    usePitchPerformanceStore.setState({
      selectedPitchId: '',
      selectedMsHash: '',
    });

    usePitchPerformanceStore.getState().generateHwDiagnostic(props.hwd);
  }, [props.hwd._id, filters]);

  const onDrillDown = (id: string) => {
    // keep track of what groups were checked off before drilling down so we can restore them after drilling back up
    checkedGroups.current = checkedIDs;

    usePitchPerformanceStore.setState({ selectedPitchId: id });

    // setting checked here will show only the drilled down pitch in the break/plate plots
    resetChecked();
    setChecked([id], true);
  };

  const onDrillup = () => {
    // restore the groups that were checked off before drilling down
    resetChecked();
    setChecked(checkedGroups.current, true);
  };

  return (
    <ErrorBoundary identifier="HwDiagnosticDetails">
      <Flex
        direction="column"
        gap={RADIX.FLEX.GAP.MD}
        className={RADIX.VFLEX.GROW}
      >
        <Flex direction="column" gap="5">
          <Flex justify="between" align="center">
            <Flex gap={RADIX.FLEX.GAP.SM} align="center">
              <CommonBackArrow onClick={onBack} />

              <Heading size="4">{t('common.hardware-diagnostic')}</Heading>

              <Badge color={RADIX.COLOR.NEUTRAL}>
                {format(
                  parseISO(collection._created),
                  LOCAL_PRETTY_DATETIME_FORMAT
                )}
              </Badge>
            </Flex>

            <Box>
              <PitchPerfDisplayOptions />
            </Box>
          </Flex>

          <PitchPerfPlots
            groups={hwdGroups}
            refGroups={rtGroups}
            isHwDiagnostic
          />

          {/* keep tables in the DOM to persist any internal state they're maintaining (e.g. search queries) */}
          <Box className={!selectedPitchId ? 'hidden' : ''}>
            <PitchPerfShotTableHoC onDrillUp={onDrillup} />
          </Box>

          <Box className={selectedPitchId ? 'hidden' : ''}>
            <PitchPerfGroupTable
              onDrillDown={onDrillDown}
              machine={props.machine}
            />
          </Box>
        </Flex>
      </Flex>
    </ErrorBoundary>
  );
};
